import {
  CHUNK_SIZE,
  chunkKeyInt,
  createNoiseGenerator,
  TILE_SIZE,
  TileType,
  type TileTypeValue,
} from '@dreamgame/shared';
import { Graphics } from 'pixi.js';
import { type ChunkWithObjects, getAllChunks } from '../../state/chunks';
import { TREE_OBJECT_TYPES, treeJitterX, treeJitterY } from './forestUtils';
import type { VisibleChunk } from './types';

// ============================================================================
// ForestEffectsLayer - Canopy shadows + dappled light + ground decorations
// ============================================================================
// Renders semi-transparent dark ellipses under trees (canopy shadow),
// warm-green dappled light patches, and varied ground decorations on forest
// tiles. Follows WaterEffectsLayer pattern: single Graphics, frame-level
// caching, only recomputes when visible chunks or chunk data change.
// ============================================================================

let graphics: Graphics;

export const createForestEffectsLayer = (): Graphics => {
  graphics = new Graphics();
  return graphics;
};

// Forest tile types that get ground decorations
const FOREST_TILES: ReadonlySet<TileTypeValue> = new Set([
  TileType.FOREST_FLOOR,
  TileType.FOREST_GRASS,
  TileType.PINE_NEEDLES,
]);

// Canopy shadow config (scaled up to match oversized tree sprites)
const SHADOW_RADIUS_X = 42;
const SHADOW_RADIUS_Y = 36;
const SHADOW_COLOR = 0x000000;
const SHADOW_ALPHA = 0.2;
const SHADOW_SOFT_ALPHA = 0.1;
// Offset so shadow sits at tree base (trees are 1x2 tiles, shadow at bottom tile)
const SHADOW_OFFSET_Y = TILE_SIZE;

// Dappled light config (warm-green sunlight patches)
const DAPPLE_COLOR = 0x8ab848;
const DAPPLE_ALPHA_MIN = 0.05;
const DAPPLE_ALPHA_RANGE = 0.06;
const DAPPLE_RADIUS_MIN = 4;
const DAPPLE_RADIUS_RANGE = 5;

// Macro ground tint patches to break tile repetition
const PATCH_COLORS = [0x2b4821, 0x355226, 0x415f2f, 0x665a31] as const;
const PATCH_ALPHA_MIN = 0.05;
const PATCH_ALPHA_RANGE = 0.07;
const PATCH_RADIUS_X_MIN = 14;
const PATCH_RADIUS_X_RANGE = 22;
const PATCH_RADIUS_Y_MIN = 10;
const PATCH_RADIUS_Y_RANGE = 18;

// Ground decoration config - varied shapes
const DECORATION_COLORS = [0x4d6d30, 0x6f8d45, 0x49603f, 0x83683d, 0x8da851] as const;
const DECORATION_ALPHA = 0.35;

// Tree-base glue pass: hides hard sprite-to-ground seams with clustered tufts.
const TREE_BASE_DARK = 0x2f4725;
const TREE_BASE_GREEN = 0x54773a;
const TREE_BASE_ALPHA = 0.28;

// Decoration shape types (deterministic per-tile)
const DECOR_SHAPE_MOSS = 0;
const DECOR_SHAPE_LEAVES = 1;
const DECOR_SHAPE_PATCH = 2;
const DECOR_SHAPE_GRASS = 3;
const DECOR_SHAPE_TWIG = 5;

// Low-frequency noise fields for macro forest richness
const _patchNoise = createNoiseGenerator({ seed: 9113 });
const _decorNoise = createNoiseGenerator({ seed: 9127 });

// PERFORMANCE: Frame-level cache (same pattern as WaterEffectsLayer)
let _cachedChunksRef: ReadonlyMap<number, ChunkWithObjects> | null = null;
let _cachedChunksSize = 0;
let _cachedVisibleHash = 0;

// PERFORMANCE: Preallocated temp for chunkKeyInt
const _chunkKeyTemp = { x: 0, y: 0 };

// Hash visible chunk coordinates for cache invalidation
const computeVisibleChunksHash = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): number => {
  let hash = visibleChunks.length;
  for (let i = 0; i < visibleChunks.length; i++) {
    const vc = visibleChunks[i];
    if (vc) {
      hash = (hash * 31 + vc.x) | 0;
      hash = (hash * 31 + vc.y) | 0;
    }
  }
  return hash;
};

// Deterministic spatial hash for decoration placement
const decorationHash = ({
  tileX,
  tileY,
  seed,
}: {
  tileX: number;
  tileY: number;
  seed: number;
}): number => {
  return (((tileX * 48271 + seed * 7) ^ (tileY * 16807)) >>> 0) % 1000;
};

const patchNoiseValue = ({
  worldTileX,
  worldTileY,
}: {
  worldTileX: number;
  worldTileY: number;
}): number => {
  return (_patchNoise.noise2D({ x: worldTileX * 0.06, y: worldTileY * 0.06 }) + 1) / 2;
};

const decorDensityValue = ({
  worldTileX,
  worldTileY,
}: {
  worldTileX: number;
  worldTileY: number;
}): number => {
  return (_decorNoise.noise2D({ x: worldTileX * 0.11, y: worldTileY * 0.11 }) + 1) / 2;
};

// Draw canopy shadows for tree objects in a chunk (aligned with tree jitter)
const drawChunkCanopyShadows = ({ chunk }: { chunk: ChunkWithObjects }): void => {
  for (const obj of chunk.objects) {
    if (!TREE_OBJECT_TYPES.has(obj.objectType)) {
      continue;
    }

    // Shadow center at the base of the tree, offset by same jitter as the sprite
    const jx = treeJitterX({ tileX: obj.tileX, tileY: obj.tileY });
    const jy = treeJitterY({ tileX: obj.tileX, tileY: obj.tileY });
    const centerX = obj.tileX * TILE_SIZE + TILE_SIZE / 2 + jx;
    const centerY = obj.tileY * TILE_SIZE + SHADOW_OFFSET_Y + TILE_SIZE / 2 + jy;

    graphics.setFillStyle({ color: SHADOW_COLOR, alpha: SHADOW_ALPHA });
    graphics.ellipse(centerX, centerY, SHADOW_RADIUS_X, SHADOW_RADIUS_Y);
    graphics.fill();
    graphics.setFillStyle({ color: SHADOW_COLOR, alpha: SHADOW_SOFT_ALPHA });
    graphics.ellipse(centerX, centerY, SHADOW_RADIUS_X * 1.35, SHADOW_RADIUS_Y * 1.3);
    graphics.fill();
  }
};

const drawTreeBaseUndergrowth = ({ chunk }: { chunk: ChunkWithObjects }): void => {
  for (const obj of chunk.objects) {
    if (!TREE_OBJECT_TYPES.has(obj.objectType)) {
      continue;
    }

    const jx = treeJitterX({ tileX: obj.tileX, tileY: obj.tileY });
    const jy = treeJitterY({ tileX: obj.tileX, tileY: obj.tileY });
    const baseX = obj.tileX * TILE_SIZE + TILE_SIZE / 2 + jx;
    const baseY = obj.tileY * TILE_SIZE + SHADOW_OFFSET_Y + TILE_SIZE * 0.72 + jy;
    const tuftCount = 3 + (decorationHash({ tileX: obj.tileX, tileY: obj.tileY, seed: 701 }) % 3);

    for (let i = 0; i < tuftCount; i++) {
      const angleSeed = decorationHash({ tileX: obj.tileX, tileY: obj.tileY, seed: 709 + i * 7 });
      const distSeed = decorationHash({ tileX: obj.tileX, tileY: obj.tileY, seed: 719 + i * 7 });
      const sizeSeed = decorationHash({ tileX: obj.tileX, tileY: obj.tileY, seed: 727 + i * 7 });
      const colorSeed = decorationHash({ tileX: obj.tileX, tileY: obj.tileY, seed: 733 + i * 7 });

      const angle = (angleSeed / 1000) * Math.PI * 2;
      const distance = 7 + (distSeed % 16);
      const x = baseX + Math.cos(angle) * distance;
      const y = baseY + Math.sin(angle) * distance * 0.46;
      const radiusX = 3 + (sizeSeed % 4);
      const radiusY = 2 + (Math.floor(sizeSeed / 9) % 3);
      const color = colorSeed > 480 ? TREE_BASE_GREEN : TREE_BASE_DARK;

      graphics.setFillStyle({ color, alpha: TREE_BASE_ALPHA });
      graphics.ellipse(x, y, radiusX, radiusY);
      graphics.fill();
    }
  }
};

// Draw low-frequency organic patches to suppress grid-like tile repetition
const drawChunkMacroGroundPatches = ({
  chunk,
  chunkX,
  chunkY,
}: {
  chunk: ChunkWithObjects;
  chunkX: number;
  chunkY: number;
}): void => {
  const chunkWorldX = chunkX * CHUNK_SIZE * TILE_SIZE;
  const chunkWorldY = chunkY * CHUNK_SIZE * TILE_SIZE;

  for (let localY = 0; localY < CHUNK_SIZE; localY++) {
    for (let localX = 0; localX < CHUNK_SIZE; localX++) {
      const tileIndex = localY * CHUNK_SIZE + localX;
      const tileType = (chunk.tiles[tileIndex] ?? TileType.VOID) as TileTypeValue;
      if (!FOREST_TILES.has(tileType)) {
        continue;
      }

      const worldTileX = chunkX * CHUNK_SIZE + localX;
      const worldTileY = chunkY * CHUNK_SIZE + localY;

      // ~10% of forest tiles become patch anchors
      const anchor = decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: 601 });
      if (anchor > 100) {
        continue;
      }

      const worldX = chunkWorldX + localX * TILE_SIZE;
      const worldY = chunkWorldY + localY * TILE_SIZE;
      const noise = patchNoiseValue({ worldTileX, worldTileY });

      const cx =
        worldX + (decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: 603 }) % TILE_SIZE);
      const cy =
        worldY + (decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: 607 }) % TILE_SIZE);
      const radiusX =
        PATCH_RADIUS_X_MIN +
        (decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: 611 }) %
          PATCH_RADIUS_X_RANGE);
      const radiusY =
        PATCH_RADIUS_Y_MIN +
        (decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: 613 }) %
          PATCH_RADIUS_Y_RANGE);
      const color =
        PATCH_COLORS[
          (decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: 617 }) +
            Math.floor(noise * 7)) %
            PATCH_COLORS.length
        ] ?? PATCH_COLORS[0];
      const alpha = PATCH_ALPHA_MIN + noise * PATCH_ALPHA_RANGE;

      graphics.setFillStyle({ color, alpha });
      graphics.ellipse(cx, cy, radiusX, radiusY);
      graphics.fill();
    }
  }
};

// Draw dappled light patches on forest tiles
const drawChunkDappledLight = ({
  chunk,
  chunkX,
  chunkY,
}: {
  chunk: ChunkWithObjects;
  chunkX: number;
  chunkY: number;
}): void => {
  const chunkWorldX = chunkX * CHUNK_SIZE * TILE_SIZE;
  const chunkWorldY = chunkY * CHUNK_SIZE * TILE_SIZE;

  for (let localY = 0; localY < CHUNK_SIZE; localY++) {
    for (let localX = 0; localX < CHUNK_SIZE; localX++) {
      const tileIndex = localY * CHUNK_SIZE + localX;
      const tileType = (chunk.tiles[tileIndex] ?? TileType.VOID) as TileTypeValue;

      if (!FOREST_TILES.has(tileType)) {
        continue;
      }

      const worldTileX = chunkX * CHUNK_SIZE + localX;
      const worldTileY = chunkY * CHUNK_SIZE + localY;
      const noise = patchNoiseValue({ worldTileX, worldTileY });

      // Light shafts prefer clearings and edges (higher patch noise)
      const h = decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: 99 });
      const threshold = 170 + Math.floor(noise * 260);
      if (h > threshold) {
        continue;
      }

      const worldX = chunkWorldX + localX * TILE_SIZE;
      const worldY = chunkWorldY + localY * TILE_SIZE;
      const px = decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: 100 });
      const py = decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: 101 });

      const cx = worldX + (px % (TILE_SIZE - 8)) + 4;
      const cy = worldY + (py % (TILE_SIZE - 6)) + 3;
      const radiusX =
        DAPPLE_RADIUS_MIN +
        (decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: 103 }) % DAPPLE_RADIUS_RANGE);
      const radiusY =
        DAPPLE_RADIUS_MIN +
        (decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: 107 }) % DAPPLE_RADIUS_RANGE);
      const alpha = DAPPLE_ALPHA_MIN + noise * DAPPLE_ALPHA_RANGE;

      graphics.setFillStyle({ color: DAPPLE_COLOR, alpha });
      graphics.ellipse(cx, cy, radiusX, radiusY);
      graphics.fill();
      graphics.setFillStyle({ color: DAPPLE_COLOR, alpha: alpha * 0.6 });
      graphics.ellipse(cx + 2, cy + 1, Math.max(2, radiusX - 2), Math.max(2, radiusY - 2));
      graphics.fill();
    }
  }
};

// Draw a single ground decoration shape
const drawDecorationShape = ({
  x,
  y,
  shape,
  color,
  variation,
}: {
  x: number;
  y: number;
  shape: number;
  color: number;
  variation: number;
}): void => {
  if (shape === DECOR_SHAPE_TWIG) {
    const length = 3 + (variation % 5);
    const horizontal = variation % 2 === 0 ? length : -length;
    const vertical = 1 + (Math.floor(variation / 7) % 3);
    graphics.setStrokeStyle({ width: 1, color, alpha: DECORATION_ALPHA * 0.9 });
    graphics.moveTo(x, y);
    graphics.lineTo(x + horizontal, y + vertical);
    graphics.stroke();
    if (variation % 3 === 0) {
      graphics.moveTo(x + horizontal * 0.45, y + vertical * 0.45);
      graphics.lineTo(x + horizontal * 0.55, y - vertical * 0.2);
      graphics.stroke();
    }
    return;
  }

  graphics.setFillStyle({ color, alpha: DECORATION_ALPHA });
  if (shape === DECOR_SHAPE_MOSS) {
    graphics.ellipse(x + 2, y + 1.5, 2.2, 1.6);
    graphics.fill();
    return;
  }
  if (shape === DECOR_SHAPE_LEAVES) {
    graphics.ellipse(x + 1.4, y + 1.3, 1.6, 1.1);
    graphics.fill();
    graphics.ellipse(x + 3.6, y + 1.8, 1.7, 1.2);
    graphics.fill();
    return;
  }
  if (shape === DECOR_SHAPE_PATCH) {
    graphics.rect(x, y, 4, 3);
    graphics.fill();
    return;
  }
  if (shape === DECOR_SHAPE_GRASS) {
    graphics.rect(x, y, 1, 5);
    graphics.fill();
    graphics.rect(x + 2, y + 1, 1, 4);
    graphics.fill();
    return;
  }

  // Pebble fallback
  graphics.circle(x + 1.8, y + 1.6, 1.3);
  graphics.fill();
};

// Draw ground decorations on forest tiles in a chunk
const drawChunkGroundDecorations = ({
  chunk,
  chunkX,
  chunkY,
}: {
  chunk: ChunkWithObjects;
  chunkX: number;
  chunkY: number;
}): void => {
  const chunkWorldX = chunkX * CHUNK_SIZE * TILE_SIZE;
  const chunkWorldY = chunkY * CHUNK_SIZE * TILE_SIZE;

  for (let localY = 0; localY < CHUNK_SIZE; localY++) {
    for (let localX = 0; localX < CHUNK_SIZE; localX++) {
      const tileIndex = localY * CHUNK_SIZE + localX;
      const tileType = (chunk.tiles[tileIndex] ?? TileType.VOID) as TileTypeValue;

      if (!FOREST_TILES.has(tileType)) {
        continue;
      }

      const worldTileX = chunkX * CHUNK_SIZE + localX;
      const worldTileY = chunkY * CHUNK_SIZE + localY;
      const worldX = chunkWorldX + localX * TILE_SIZE;
      const worldY = chunkWorldY + localY * TILE_SIZE;
      const decorDensity = decorDensityValue({ worldTileX, worldTileY });

      // Patchy decor density gives richer clumps and sparse clearings.
      const baseCount = decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: 0 }) % 4;
      const scaledCount = Math.floor(baseCount * (0.6 + decorDensity * 0.9));
      const denseBonus =
        decorDensity > 0.75 &&
        decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: 21 }) < 140
          ? 1
          : 0;
      const decorCount = Math.min(5, scaledCount + denseBonus);

      for (let d = 0; d < decorCount; d++) {
        // Deterministic position within tile
        const px = decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: d * 5 + 1 });
        const py = decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: d * 5 + 2 });
        const ci = decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: d * 5 + 3 });
        const vi = decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: d * 5 + 4 });
        const si = decorationHash({ tileX: worldTileX, tileY: worldTileY, seed: d * 5 + 5 });

        const dotX = worldX + (px % (TILE_SIZE - 6)) + 2;
        const dotY = worldY + (py % (TILE_SIZE - 8)) + 2;
        const color = DECORATION_COLORS[ci % DECORATION_COLORS.length] ?? DECORATION_COLORS[0];
        const shape = si % 6;

        drawDecorationShape({ x: dotX, y: dotY, shape, color, variation: vi });
      }
    }
  }
};

export const drawForestEffects = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  const loadedChunks = getAllChunks();

  // PERFORMANCE: Skip when nothing changed
  const visibleHash = computeVisibleChunksHash({ visibleChunks });
  const needsRecompute =
    loadedChunks !== _cachedChunksRef ||
    loadedChunks.size !== _cachedChunksSize ||
    visibleHash !== _cachedVisibleHash;

  if (!needsRecompute) {
    return;
  }

  graphics.clear();

  for (const { x: chunkX, y: chunkY } of visibleChunks) {
    _chunkKeyTemp.x = chunkX;
    _chunkKeyTemp.y = chunkY;
    const chunk = loadedChunks.get(chunkKeyInt(_chunkKeyTemp));
    if (!chunk) {
      continue;
    }

    // Draw canopy shadows first (behind everything)
    drawChunkCanopyShadows({ chunk });
    // Macro tint patches add low-frequency forest floor variation
    drawChunkMacroGroundPatches({ chunk, chunkX, chunkY });
    // Then dappled light patches
    drawChunkDappledLight({ chunk, chunkX, chunkY });
    // Tree-base undergrowth welds tree sprites into the ground
    drawTreeBaseUndergrowth({ chunk });
    // Then ground decorations on top
    drawChunkGroundDecorations({ chunk, chunkX, chunkY });
  }

  _cachedChunksRef = loadedChunks;
  _cachedChunksSize = loadedChunks.size;
  _cachedVisibleHash = visibleHash;
};
